/**
 * LegFixReturnBanner · Feb 17 · v28
 *
 * When a founder taps the "Mark leg as fixed → Re-run Health check"
 * deep-link inside a <WhyLegPopover />, the destination URL carries a
 * `?legFix=<legKey>` query param. This banner renders bottom-right on
 * ANY route the founder lands on and offers a one-click way to close
 * the loop:
 *   - "Fixed it — Re-run Health check" → recomputes the correct hero
 *     family (Plan Health for plan legs; Operating Health snapshot
 *     for operating legs; otherwise a business refetch is enough
 *     since the score is derived client-side).
 *   - "Not yet" → clears the query param so the banner disappears.
 *
 * Only visible when `?legFix=` is present AND we can derive a
 * `business_id` from either the path (/business/{id}/...) or a
 * `?business=` query param (compliance + grants).
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { CheckCircle2, RefreshCw, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/apiClient";

// leg-key → hero family → API to re-run. Only Plan Health has a real
// server-side re-run endpoint; every other hero is a client-side
// rollup that re-derives on a fresh business fetch (their snapshot
// endpoints only persist a pre-computed pct, they don't recompute).
const PLAN_LEG_KEYS = ["plan-runway", "plan-budget", "plan-revenue", "plan-roadmap"];

// Human labels for the "You just fixed …" line.
const LEG_LABELS = {
  "plan-runway": "Plan Runway",
  "plan-budget": "Budget completeness",
  "plan-revenue": "Revenue realism",
  "plan-roadmap": "Roadmap balance",
  "launch-plan": "Launch · Plan progress",
  "launch-roadmap": "Launch · Roadmap progress",
  "launch-compliance": "Launch · Compliance",
  "business-launch": "Business · Launch readiness",
  "business-funding": "Business · Funding",
  "business-marketing": "Business · Marketing",
  "operating-revenue": "Operating · Revenue trend",
  "operating-funding-health": "Operating · Financial health",
  "operating-marketing": "Operating · Marketing",
  "operating-launch-health": "Operating · Launch health",
  "funding-runway": "Funding · Runway",
  "funding-capital-stack": "Funding · Capital stack",
  "funding-net-vs-plan": "Funding · Net vs plan",
  "funding-marketing": "Funding · Marketing",
  "marketing-audience": "Marketing · Audience",
  "marketing-reach": "Marketing · Reach",
  "marketing-conversion": "Marketing · Conversion",
  "marketing-pipeline": "Marketing · Pipeline",
};

export function extractBusinessId(pathname, search) {
  // /business/{id}/... — first-match path segment
  const m = /\/business\/([A-Za-z0-9_-]+)/.exec(pathname || "");
  if (m) return m[1];
  // /compliance?business={id} or /grants?business={id}
  try {
    const q = new URLSearchParams(search || "");
    const b = q.get("business");
    if (b) return b;
  } catch {
    // no-op
  }
  return null;
}

export function pickReRunKind(legKey) {
  if (!legKey) return "none";
  if (PLAN_LEG_KEYS.includes(legKey)) return "plan-health";
  return "refetch";
}

export default function LegFixReturnBanner() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const legKey = params.get("legFix");
  const bid = extractBusinessId(location.pathname, location.search);
  const [busy, setBusy] = useState(false);

  // Reset busy state when the founder navigates elsewhere.
  useEffect(() => {
    setBusy(false);
  }, [location.pathname]);

  if (!legKey || !bid) return null;

  const label = LEG_LABELS[legKey] || legKey;
  const kind = pickReRunKind(legKey);

  const clearParam = () => {
    const next = new URLSearchParams(location.search);
    next.delete("legFix");
    const qs = next.toString();
    navigate(
      { pathname: location.pathname, search: qs ? `?${qs}` : "", hash: location.hash },
      { replace: true },
    );
  };

  const handleRerun = async () => {
    setBusy(true);
    try {
      if (kind === "plan-health") {
        await api.post(`/businesses/${bid}/health-check`);
        toast.success(`${label} re-scored — check your Hub for the updated Plan Health.`);
      } else {
        // Every non-Plan hero (Launch / Business / Operating / Funding
        // / Marketing) is a client-side rollup — the daily snapshot
        // endpoints only PERSIST a pre-computed pct, they don't
        // recompute anything server-side. A fresh business fetch is
        // enough: the Hub's next render re-derives every leg.
        await api.get(`/businesses/${bid}`);
        toast.success(`${label} rechecked — your Hub score has refreshed.`);
      }
      clearParam();
      // Feb 2026 — belt-and-suspenders refresh. Even though the Hub's
      // `useEffect` fires on mount, if the browser keeps a stale
      // <BusinessHubPage /> mounted (e.g. tab-switching or a subtle
      // React StrictMode replay), the Roadmap / Runway pill could
      // read the pre-rerun snapshot. Appending a `_r=<timestamp>`
      // param guarantees the Hub sees a NEW search string and
      // refetches fresh.
      navigate(`/business/${bid}?_r=${Date.now()}`);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          "Couldn't re-run the Health check — try again from the Hub.",
      );
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="leg-fix-return-banner"
      className="fixed bottom-4 right-4 z-50 w-[min(360px,calc(100vw-2rem))] border-2 border-[#4A4458] bg-white shadow-lg"
    >
      <div className="border-b border-neutral-200 px-3 py-2 flex items-center justify-between gap-2 bg-[#F5F0FB]">
        <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.2em] text-[#4A4458]">
          <CheckCircle2 className="w-3.5 h-3.5 text-[#7C5FB0]" aria-hidden />
          Fixed it?
        </div>
        <button
          type="button"
          onClick={clearParam}
          data-testid="leg-fix-return-banner-dismiss"
          aria-label="Dismiss re-run banner"
          className="rounded-full opacity-60 hover:opacity-100 focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-[#7C5FB0]"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="px-3 py-2 space-y-1">
        <div
          data-testid="leg-fix-return-banner-leg"
          className="text-xs text-neutral-700"
        >
          You landed here to fix{" "}
          <span className="font-bold text-[#2D2A3E]">{label}</span>.
        </div>
        <div className="text-[11px] text-neutral-500">
          When you&apos;re done editing, re-run the Health check to see the leg
          climb back into the green.
        </div>
      </div>
      <div className="border-t border-neutral-200 px-3 py-2 flex items-center gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={handleRerun}
          data-testid="leg-fix-return-banner-rerun-btn"
          className="inline-flex items-center gap-1.5 rounded-none border border-[#7C5FB0] bg-[#7C5FB0] px-3 py-1.5 text-[11px] font-black uppercase tracking-wider text-white hover:bg-[#5E4689] disabled:opacity-60 disabled:cursor-not-allowed transition-colors flex-1 justify-center"
        >
          <RefreshCw
            className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`}
            aria-hidden
          />
          {busy ? "Re-running…" : "Fixed it · Re-run"}
        </button>
        <Link
          to={`/business/${bid}?full=1`}
          onClick={clearParam}
          data-testid="leg-fix-return-banner-hub-link"
          className="text-[11px] font-bold uppercase tracking-wider text-[#7C5FB0] hover:underline"
        >
          Back to Hub
        </Link>
      </div>
    </div>
  );
}
